import React, { useEffect, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import axios from "axios";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { Phone } from "lucide-react";

const API_BASE_URL = "https://api.havirkesht.ir";
const WALLET_URL = `${API_BASE_URL}/contractor-settings/wallet`;
const WALLET_PHONE_URL = `${API_BASE_URL}/contractor-settings/wallet-phone`;

const ContractorPhoneSettings = () => {
  document.title = "تنظیم شماره همراه پیمانکار | سامانه هاویر کشت";

  const token = localStorage.getItem("app_atkey");

  // state‌ها
  const [current, setCurrent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  // ---- دریافت شماره همراه موجود ----
  useEffect(() => {
    const fetchPhoneSettings = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const res = await axios.get(WALLET_URL, { headers: authHeaders });
        setCurrent(res?.data);
      } catch (error: any) {
        setCurrent(null);
        setErrorMessage("خطا در دریافت تنظیمات شماره همراه");
        console.error("Error fetching phone settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoneSettings();
  }, []);

  // ---- validation ----
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      phone_number: current?.phone_number || "",
    },
    validationSchema: Yup.object({
      phone_number: Yup.string()
        .matches(/^09\d{9}$/, "شماره همراه باید 11 رقمی و با 09 شروع شود")
        .required("شماره همراه نباید خالی باشد"),
    }),
    onSubmit: async (values) => {
      const result = await Swal.fire({
        title: "تأیید ذخیره",
        text: `آیا از ذخیره شماره همراه ${values.phone_number} اطمینان دارید؟`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "بله",
        cancelButtonText: "خیر",
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;

      setIsSaving(true);
      try {
        const res = await axios.put(
          WALLET_PHONE_URL,
          { phone_number: values.phone_number },
          { headers: { ...authHeaders, "Content-Type": "application/json" } }
        );
        setCurrent(res?.data);
        toast.success("شماره همراه با موفقیت ذخیره شد");
      } catch (error: any) {
        if (error?.response?.status === 403) {
          toast.error("شما اجازه ذخیره این تنظیمات را ندارید");
        } else {
          toast.error("مشکلی در ذخیره شماره همراه رخ داد");
        }
        console.error("Error saving phone:", error);
      } finally {
        setIsSaving(false);
      }
    },
  });

  // ---- حذف شماره همراه ----
  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "حذف شماره همراه",
      text: "آیا از حذف شماره همراه اطمینان دارید؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "بله، حذف کن",
      cancelButtonText: "خیر",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    setIsSaving(true);
    try {
      await axios.delete(WALLET_PHONE_URL, { headers: authHeaders });
      setCurrent({ ...current, phone_number: null });
      validation.setFieldValue("phone_number", "");
      toast.success("شماره همراه با موفقیت حذف شد");
    } catch (error: any) {
      const detailCode = error?.response?.data?.detail?.code;
      if (detailCode === "payment_pending") {
        toast.error("ابتدا وضعیت پرداخت قبلی بررسی شود");
      } else if (error?.response?.status === 409) {
        toast.error("برای حذف شماره همراه، ابتدا ادمین کل باید پرداخت بانکی این پیمانکار را غیرفعال کند");
      } else {
        toast.error("مشکلی در حذف شماره همراه رخ داد");
      }
      console.error("Error deleting phone:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="تنظیم شماره همراه پیمانکار" title="داده های اولیه" />
      <ToastContainer closeButton={false} limit={1} />
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
            <div className="xl:col-span-7">
              <h6 className="mb-4 text-15 dark:text-white">
                شماره همراه پیمانکار برای پرداخت بانکی
              </h6>

              {/* نمایش اطلاعات فعلی */}
              <div className="p-4 mb-4 rounded-md bg-slate-100 dark:bg-zink-600 dark:text-white">
                {isLoading ? (
                  <span>در حال دریافت اطلاعات...</span>
                ) : errorMessage ? (
                  <span className="text-red-500">{errorMessage}</span>
                ) : (
                  <div className="flex flex-col gap-2">
                    <span>
                      شماره همراه:{" "}
                      <b>{current?.phone_number || "ثبت نشده"}</b>
                    </span>
                    {current?.wallet_title && (
                      <span>
                        عنوان کیف پول: <b>{current.wallet_title}</b>
                      </span>
                    )}
                    <span className="text-sm text-slate-600 dark:text-zink-200">
                      وضعیت پرداخت بانکی:{" "}
                      <b>
                        {current?.bank_payments_enabled ? "فعال" : "غیرفعال"}
                      </b>
                    </span>
                  </div>
                )}
              </div>

              {/* فرم ثبت/ویرایش */}
              <form onSubmit={validation.handleSubmit}>
                <label
                  htmlFor="phone_number"
                  className="inline-block mb-2 text-base font-medium dark:text-white"
                >
                  شماره همراه (09xxxxxxxxx):
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="phone_number"
                    name="phone_number"
                    autoComplete="off"
                    disabled={isLoading}
                    className="form-input w-full p-2 pl-8 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-[#132337]"
                    placeholder="09123456789"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.phone_number}
                  />
                  <span className="absolute inset-y-0 left-2 flex items-center text-gray-400">
                    <Phone size={15} />
                  </span>
                </div>
                {validation.touched.phone_number &&
                  validation.errors.phone_number && (
                    <p className="mt-2 text-red-400">
                      {typeof validation.errors.phone_number === 'string'
                        ? validation.errors.phone_number
                        : 'خطای نامعتبر'}
                    </p>
                  )}

                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={isSaving || isLoading}
                    className="px-4 py-2 font-bold text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isSaving ? "در حال ذخیره..." : "ذخیره"}
                  </button>

                  {current?.phone_number && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isSaving || isLoading}
                      className="px-4 py-2 font-bold text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-50"
                    >
                      {isSaving ? "در حال حذف..." : "حذف"}
                    </button>
                  )}
                </div>
              </form>

              <p className="mt-4 text-sm text-slate-500 dark:text-zink-200">
                این شماره همراه برای انتقال وجه از حساب پیمانکار به حساب کشاورزان استفاده می‌شود. فقط شماره‌های ایرانی با فرمت 09xxxxxxxxx پذیرفته می‌شود.
              </p>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ContractorPhoneSettings;
